// Building — live workflow progress driven by /api/workflows/:brand/:id/stream.
// Layout from the design's building.jsx; steps come from the real engine
// (brief / build-funnel / pick-scripts / videos / publish).
import { useEffect, useMemo, useState } from 'react';
import { PALETTE } from '../palette.js';
import { Pill, LiveDot, Button } from '../components/primitives.jsx';
import { IconCheck, IconSpark, IconPlay } from '../components/icons.jsx';
import { api } from '../api.js';

// Plain-English copy keyed by step.key — backend labels are utilitarian, the
// design wants warmer phrasing here.
const STEP_COPY = {
  'brief':        { label: 'Writing your marketing plan',      detail: 'Reading your brand research and shaping the angle.' },
  'build-funnel': { label: 'Building your lead funnel',         detail: 'A 5-question quiz that qualifies homeowners before they reach you.' },
  'pick-scripts': { label: 'Choosing your ad scripts',          detail: '5 angles, tested hooks, built for Facebook and Instagram reels.' },
  'videos':       { label: 'Filming your videos',               detail: 'Voiceovers in a warm, trustworthy tone. Captions baked in.' },
  'publish':      { label: 'Publishing to Facebook & Instagram', detail: 'Your campaign goes live in your Meta Business Manager.' },
};

function Ring({ pct, size = 40, stroke = 3 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} style={{ display: 'block' }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={PALETTE.surfaceAlt} strokeWidth={stroke}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={PALETTE.accent} strokeWidth={stroke}
        strokeDasharray={c} strokeDashoffset={c * (1 - pct)} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{ transition: 'stroke-dashoffset .3s' }}
      />
    </svg>
  );
}

function fmtDuration(ms) {
  if (!ms || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function elapsedSec(startedAt) {
  if (!startedAt) return 0;
  return Math.max(0, Math.round((Date.now() - new Date(startedAt).getTime()) / 1000));
}

export default function Building({ navigate, params }) {
  const [brandSlug, jobId] = params; // route #/building/:brand/:id
  const [job, setJob] = useState(null);
  const [tick, setTick] = useState(0); // re-render every 1s so live durations update
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!brandSlug || !jobId) return;
    let es = null;
    api.get(`/api/workflows/${brandSlug}/${jobId}`)
      .then((r) => setJob(r.job))
      .catch((e) => setError(e.message));

    es = api.stream(`/api/workflows/${brandSlug}/${jobId}/stream`, {
      onMessage: (ev) => {
        if (ev?.type === 'snapshot' && ev.job) setJob(ev.job);
        else if (ev?.type === 'step-update') {
          setJob((prev) => prev ? applyStepUpdate(prev, ev) : prev);
        }
        else if (ev?.type === 'job-update') {
          setJob((prev) => prev ? { ...prev, ...ev } : prev);
        }
      },
      onError: () => { /* SSE auto-reconnects; ignore */ },
    });

    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { es?.close(); clearInterval(iv); };
  }, [brandSlug, jobId]);

  const steps = job?.steps || [];
  const status = job?.status || 'pending';
  const complete = status === 'done';
  const failed = status === 'failed' || status === 'canceled' || status === 'interrupted';
  const [retrying, setRetrying] = useState(false);

  async function handleRetry() {
    if (!brandSlug || !jobId || retrying) return;
    setRetrying(true);
    setError(null);
    try {
      await api.post(`/api/workflows/${brandSlug}/${jobId}/retry`);
      const r = await api.get(`/api/workflows/${brandSlug}/${jobId}`);
      setJob(r.job);
    } catch (e) {
      setError(e.message);
    } finally {
      setRetrying(false);
    }
  }

  const overallPct = useMemo(() => {
    if (!steps.length) return 0;
    const done = steps.filter((s) => s.status === 'done').length;
    const running = steps.find((s) => s.status === 'running');
    let runFrac = 0;
    if (running?.children?.length) {
      const childDone = running.children.filter((c) => c.status === 'done').length;
      runFrac = childDone / running.children.length;
    } else if (running) {
      runFrac = 0.5;
    }
    return Math.min(1, (done + runFrac) / steps.length);
  }, [steps, tick]);

  const business = job?.brandName || job?.brandSlug || 'Your campaign';
  const project = job?.projectType || '';
  const offer = job?.offer || '';

  return (
    <div style={{ flex: 1, background: PALETTE.bg, overflow: 'auto', fontFamily: 'Inter, sans-serif', color: PALETTE.ink, height: '100vh' }}>
      <div style={{
        height: 56, padding: '0 32px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', borderBottom: `1px solid ${PALETTE.lineSoft}`,
      }}>
        <button onClick={() => navigate('home')} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: PALETTE.ink3, fontSize: 13, fontFamily: 'Inter, sans-serif', padding: 0,
        }}>← Home</button>
        <Pill tone={complete ? 'live' : failed ? 'warn' : 'accent'}>
          {complete ? <><LiveDot/> Live</> : failed ? status : <><IconSpark size={10}/> Building</>}
        </Pill>
      </div>

      <div style={{ maxWidth: 980, margin: '0 auto', padding: '56px 40px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'end', gap: 40, marginBottom: 48 }}>
          <div>
            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: PALETTE.accent, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 16 }}>
              {business}{project ? ` · ${project}` : ''}{offer ? ` · ${offer}` : ''}
            </div>
            <h1 style={{
              fontFamily: 'Instrument Serif, serif', fontWeight: 400,
              fontSize: 60, lineHeight: 1.02, letterSpacing: '-0.02em', margin: 0,
            }}>
              {complete ? (<>Your campaign is <em style={{ color: PALETTE.accent, fontStyle: 'italic' }}>live.</em></>)
                : failed ? (<>Something went <em style={{ color: '#A33', fontStyle: 'italic' }}>sideways.</em></>)
                : (<>We're building your campaign — <em style={{ color: PALETTE.accent, fontStyle: 'italic' }}>sit tight.</em></>)}
            </h1>
            <p style={{ fontSize: 16, color: PALETTE.ink2, lineHeight: 1.5, margin: '20px 0 0', maxWidth: 560 }}>
              {complete ? 'Your source artifacts are ready. Open the dashboard to continue.'
                : failed ? (job?.error || 'A step failed. Open the technical log for details.')
                : "Nothing you need to do. We'll keep going even if you close this tab."}
            </p>
            {error && <p style={{ color: '#A33', marginTop: 12 }}>{error}</p>}
            {failed && (
              <div style={{ marginTop: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
                <Button variant="accent" size="md" onClick={handleRetry} disabled={retrying}>
                  {retrying ? 'Retrying…' : 'Retry from where it stopped'}
                </Button>
                <span style={{ fontSize: 12, color: PALETTE.ink3 }}>
                  Picks up at the first incomplete step — finished work is preserved.
                </span>
              </div>
            )}
          </div>

          <div style={{ position: 'relative', width: 180, height: 180 }}>
            <Ring pct={overallPct} size={180} stroke={6}/>
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center' }}>
              <div>
                <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 54, lineHeight: 1, color: PALETTE.ink }}>
                  {Math.round(overallPct * 100)}<span style={{ fontSize: 22, color: PALETTE.ink3 }}>%</span>
                </div>
                <div style={{ fontSize: 11, color: PALETTE.ink3, marginTop: 6, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'JetBrains Mono, monospace' }}>
                  {complete ? 'Complete' : failed ? status : 'Working'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div style={{ background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 14, padding: 8 }}>
            {steps.map((s, i) => {
              const copy = STEP_COPY[s.key] || { label: s.label, detail: '' };
              const done = s.status === 'done';
              const active = s.status === 'running';
              const stepFailed = s.status === 'failed' || s.status === 'canceled';
              const liveSec = active ? elapsedSec(s.startedAt) : 0;
              const childDone = (s.children || []).filter((c) => c.status === 'done').length;
              const childTotal = (s.children || []).length;
              const pct = done ? 1 : active && childTotal ? (childDone / childTotal) : active ? 0.5 : 0;

              return (
                <div key={s.key} style={{
                  padding: '16px 14px', borderRadius: 10,
                  background: active ? PALETTE.accentTint : 'transparent',
                  display: 'grid', gridTemplateColumns: '28px 1fr auto', gap: 14, alignItems: 'start',
                  transition: 'background .2s',
                }}>
                  <div style={{ paddingTop: 2 }}>
                    {done ? (
                      <div style={{ width: 24, height: 24, borderRadius: '50%', background: PALETTE.success, color: '#fff', display: 'grid', placeItems: 'center' }}>
                        <IconCheck size={13} stroke={2.6}/>
                      </div>
                    ) : stepFailed ? (
                      <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#A33', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 14 }}>!</div>
                    ) : active ? (
                      <div style={{ position: 'relative', width: 24, height: 24 }}><Ring pct={pct} size={24} stroke={2.5}/></div>
                    ) : (
                      <div style={{ width: 24, height: 24, borderRadius: '50%', border: `1.5px dashed ${PALETTE.line}` }}/>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: done ? PALETTE.ink2 : PALETTE.ink, letterSpacing: '-0.01em' }}>{copy.label}</div>
                    <div style={{ fontSize: 12.5, color: PALETTE.ink3, marginTop: 3, lineHeight: 1.5 }}>{s.sublabel || copy.detail}</div>
                    {stepFailed && s.error && (
                      <div style={{ fontSize: 12, color: '#A33', marginTop: 6 }}>{s.error}</div>
                    )}
                    {active && childTotal > 0 && (
                      <div style={{ fontSize: 11.5, color: PALETTE.ink3, marginTop: 6, fontFamily: 'JetBrains Mono, monospace' }}>
                        {childDone}/{childTotal} videos rendered
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: PALETTE.ink3, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.03em', paddingTop: 4, textAlign: 'right' }}>
                    {done ? fmtDuration(s.durationMs)
                      : active ? `${liveSec}s`
                      : stepFailed ? s.status.toUpperCase()
                      : '—'}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: PALETTE.ink, color: '#FAF8F5', borderRadius: 14, padding: 20 }}>
              <div style={{ fontSize: 11, color: 'rgba(250,248,245,0.5)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14 }}>
                What we're building for you
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                {[
                  { n: '5', l: 'Ad variants' },
                  { n: '5', l: 'Video reels' },
                  { n: '1', l: 'Quiz funnel' },
                  { n: String((job?.options?.budgetMonthly || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ','), l: 'Monthly ad budget ($)' },
                ].map((s, i) => (
                  <div key={i}>
                    <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 36, color: PALETTE.accent, lineHeight: 1 }}>{s.n}</div>
                    <div style={{ fontSize: 12, color: 'rgba(250,248,245,0.7)', marginTop: 4 }}>{s.l}</div>
                  </div>
                ))}
              </div>
            </div>

            {job?.artifacts?.funnelUrl && (
              <div style={{ background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 14, padding: 18 }}>
                <div style={{ fontSize: 11, color: PALETTE.ink3, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Funnel source ready</div>
                <a href={job.artifacts.funnelUrl} target="_blank" rel="noreferrer" style={{ color: PALETTE.accent, fontWeight: 500, fontSize: 14 }}>
                  {job.artifacts.funnelUrl}
                </a>
              </div>
            )}
          </div>
        </div>

        {complete && (
          <div style={{
            marginTop: 40, padding: '28px 32px',
            background: PALETTE.success, color: '#fff', borderRadius: 14,
            display: 'grid', gridTemplateColumns: '1fr auto', gap: 20, alignItems: 'center',
          }}>
            <div>
              <div style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.8, marginBottom: 6 }}>You're live</div>
              <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 32, lineHeight: 1.1 }}>
                Campaign assets ready. Review and publish from the dashboard.
              </div>
            </div>
            <Button variant="accent" size="lg" onClick={() => navigate('campaigns')} style={{ background: '#fff', color: PALETTE.success, border: 'none' }}>
              See my campaigns →
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function applyStepUpdate(job, ev) {
  const steps = (job.steps || []).map((s) => {
    if (s.key !== ev.key) return s;
    if (ev.child) {
      return {
        ...s,
        children: (s.children || []).map((c) => c.key === ev.child.key ? { ...c, ...ev.child } : c),
      };
    }
    const next = { ...s, ...ev };
    delete next.type; // don't bleed event metadata into step state
    delete next.child;
    return next;
  });
  return { ...job, steps };
}
